import { clsx } from "clsx";
import type { ReactNode } from "react";

type Column<T> = {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  className?: string;
  align?: "left" | "right" | "center";
};

type DataTableProps<T> = {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string | number;
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
  className?: string;
  dense?: boolean;
};

const ALIGN_CLASSES: Record<NonNullable<Column<unknown>["align"]>, string> = {
  left: "text-left",
  right: "text-right",
  center: "text-center",
};

export function DataTable<T>({
  rows,
  columns,
  rowKey,
  onRowClick,
  empty,
  className,
  dense = false,
}: DataTableProps<T>) {
  if (rows.length === 0) {
    return (
      <div className="px-4 py-10 text-center text-sm text-foreground-secondary">
        {empty ?? "No rows."}
      </div>
    );
  }
  return (
    <div className={clsx("overflow-x-auto", className)}>
      <table className="w-full text-sm">
        <thead className="border-b border-border-subtle text-2xs uppercase tracking-wider text-foreground-muted">
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={clsx(
                  "px-3 py-2 font-medium",
                  ALIGN_CLASSES[col.align ?? "left"],
                  col.className,
                )}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={clsx(
                "border-b border-border-subtle/60 last:border-0",
                onRowClick && "cursor-pointer hover:bg-surface-subtle",
              )}
            >
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={clsx(
                    dense ? "px-3 py-1.5" : "px-3 py-2.5",
                    ALIGN_CLASSES[col.align ?? "left"],
                    col.className,
                  )}
                >
                  {col.cell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export type { Column };
